
import { AxiosResponse } from 'axios';
import { all, call, delay, fork, put, select, takeLatest } from 'redux-saga/effects';
import uiActions from 'services/UI/actions';
import { setLoading, setLoadingPage } from 'services/UI/sagas';
import { ISagaFunc } from 'services/actionConfigs';
import { IResponseDataBody, IResponseTableDataBody } from 'services/response';
import shopActions from 'services/shop/actions';
import { IStaffAvailableItem } from 'services/shop/types/staff';
import actions from './actions';
import apis from './apis';
import { PATH_LOADING } from './constants';
import { getPureFilterParams, getPureHistoryFilterParams } from './selectors';
import { DoClockOutPayload, IApiCheckInHistoriesParams, IApiCheckInHistoriesSummaryParams, IApiHistoryClockInOut, IFilterDataParams } from './types/api';
import { IClockInOutResData, IHistoryCheckInResData, IHistoryClockInOutResData } from './types/clock-in-out';
import { IStaffAvailableResItem } from './types/staff';
import { IBillWaitingTicketResItem } from './types/ticket';
import { ISummaryResData, IWaitingListResItem, IWaitingListServiceInfoResItem } from './types/waiting';
import userActions from './actions';
import turnActions from 'features/turn/services/actions';
import appointmentApis from 'features/appointment/services/apis';
import moment from 'moment';
import { DATE_FORMAT } from 'features/appointment/utils/format';
import { userUIActions } from 'features/user/services/reducers';
import { HOME_SORT_BY, HomeLayoutCode } from 'services/shop/constants';
import { INotification } from './types/notification';
import { ICouponPrintDiscount } from 'services/shop/types/coupon';
import { RootState } from 'store';
import { IShopAllSetting } from 'services/shop/types/setting';

const getSummaryData: ISagaFunc<IFilterDataParams> = function* ({ payload }) {
  yield setLoading(PATH_LOADING.getSummaryData, true);
  yield delay(100);
  try {
    const res: AxiosResponse<{ data: ISummaryResData }> = yield call(apis.getSummaryData, payload);
    if (res?.data?.data) {
      yield put(actions.getSummaryData.success(res.data.data));
    }
  } catch (error) { }
  finally {
    yield setLoading(PATH_LOADING.getSummaryData, false);
  }
};

const getWaitingList: ISagaFunc = function* () {
  yield setLoading(PATH_LOADING.getWaitingList, true);
  yield delay(100);
  try {
    const res: IResponseDataBody<IWaitingListServiceInfoResItem[]> = yield call(apis.getWaitingList);
    if (res?.data?.data) {
      yield put(actions.getWaitingList.success(res.data.data));
    }
  } catch (error) { }
  finally {
    yield setLoading(PATH_LOADING.getWaitingList, false);
  }
};

const getBillWaitingTicket = function* () {
  yield setLoading(PATH_LOADING.getBillWaitingTicket, true);
  yield delay(100);
  try {
    const res: IResponseDataBody<IBillWaitingTicketResItem[]> = yield call(apis.getBillWaitingTicket);
    if (res?.data?.data) {
      yield put(actions.getBillWaitingTicket.success(res.data.data));
    }
  } catch (error) { }
  finally {
    yield setLoading(PATH_LOADING.getBillWaitingTicket, false);
  }
};

const doFilterData: ISagaFunc<IFilterDataParams> = function* ({ payload }) {
  const storeParams = yield select(getPureFilterParams);
  const params = {
    ...storeParams ?? {},
    ...payload ?? {}
  };
  yield all([
    put(actions.getSummaryData.fetch(params)),
  ]);
};

const doClockIn: ISagaFunc<string | number> = function* ({ payload }) {
  yield setLoadingPage(true);
  yield delay(100);
  try {
    const res: IResponseDataBody<IClockInOutResData> = yield call(apis.doClockIn, payload);
    if (res.data.data) {
      yield put(actions.doClockIn.success(res.data.data));
    }
  } catch (error) { }
  finally {
    yield setLoadingPage(false);
    yield put(shopActions.get.staffsAvailable.fetch());
    yield put(userActions.getListAvailableWorking.fetch());
  }
};

const doClockOut: ISagaFunc<DoClockOutPayload> = function* ({ payload }) {
  yield setLoadingPage(true);
  yield delay(100);
  try {
    const { pin, onSuccess } = payload;
    const res: IResponseDataBody<IClockInOutResData> = yield call(apis.doClockOut, pin);
    if (res.data.data) {
      yield put(actions.doClockOut.success(res.data.data));
      if (onSuccess) {
        onSuccess(res.data.data);
      }
    }
  } catch (error) { }
  finally {
    yield setLoadingPage(false);
    yield put(shopActions.get.staffsAvailable.fetch());
    yield put(userActions.getListAvailableWorking.fetch());
  }
};

const getListStaffAvailable = function* () {
  yield setLoading(PATH_LOADING.getListStaffAvailable, true);
  yield delay(100);
  try {
    const res: IResponseDataBody<IStaffAvailableResItem[]> = yield call(apis.getStaffAvailable);
    if (res.data.data) {
      yield put(actions.getListStaffAvailable.success(res.data.data));
    }
  } catch (error) { }
  finally {
    yield setLoading(PATH_LOADING.getListStaffAvailable, false);
  }
};

const getHistoryClockInOut: ISagaFunc<IApiHistoryClockInOut> = function* ({ payload }) {
  yield setLoading(PATH_LOADING.getHistoryClockInOut, true);
  yield delay(100);
  try {
    const res: IResponseDataBody<IHistoryClockInOutResData> = yield call(apis.getHistoryClockInOut, payload);
    if (res.data.data) {
      yield put(actions.getHistoryClockInOut.success(res.data.data));
    }
  } catch (error) { }
  finally {
    yield setLoading(PATH_LOADING.getHistoryClockInOut, false);
  }
};

const setHistoryClockInOutParams: ISagaFunc<IApiHistoryClockInOut> = function* ({ payload }) {
  const storeParams = yield select(getPureHistoryFilterParams);
  const _params = {
    ...storeParams ?? {},
    ...payload ?? {},
  };
  yield put(actions.getHistoryClockInOut.fetch(_params));
};

const removePendingTicket: ISagaFunc<string> = function* ({ payload }) {
  yield put(uiActions.setLoadingPage(true));
  try {
    const res: IResponseDataBody<boolean> = yield call(apis.removePendingTicket, payload);
    if (res.data.data) {
      yield put(actions.activeRemoveTicket(false));
    }
  } catch (error) { }
  finally {
    yield put(uiActions.setLoadingPage(false));
  }
};

const removeWaitingTicket: ISagaFunc<string> = function* ({ payload }) {
  yield put(uiActions.setLoadingPage(true));
  try {
    const res: IResponseDataBody<boolean> = yield call(apis.removeWaitingTicket, payload);
    if (res.data.data) {
      yield put(actions.activeRemoveWaiting(false));
    }
  } catch (error) { }
  finally {
    yield put(uiActions.setLoadingPage(false));
  }
};

const getListAvailableWorking = function* () {
  yield delay(200);
  try {
    const res: IResponseDataBody<IStaffAvailableItem[]> = yield call(apis.getListAvailableWorking);
    if (res.data.data) {
      yield put(actions.getListAvailableWorking.success(res.data.data));
      yield put(turnActions.getTurnStaffsAvailable.success(res.data.data));
    }
  } catch (error) { }
};

const waitingList = function* () {

  try {
    const res: IResponseDataBody<IWaitingListServiceInfoResItem[]> = yield call(apis.getWaitingList);
    if (res?.data?.data) {
      yield put(actions.getWaitingList.success(res.data.data));
    }
  } catch (error) { }
};

const availableList = function* () {
  try {
    const res: IResponseDataBody<IStaffAvailableItem[]> = yield call(apis.getListAvailableWorking);
    if (res.data.data) {
      yield put(actions.getListAvailableWorking.success(res.data.data));
      yield put(turnActions.getTurnStaffsAvailable.success(res.data.data));
    }
  } catch (error) { }
};

const pendingTicket = function* () {
  try {
    const res: IResponseDataBody<IBillWaitingTicketResItem[]> = yield call(apis.getBillWaitingTicket);
    if (res?.data?.data) {
      yield put(actions.getBillWaitingTicket.success(res.data.data));
    }
  } catch (error) { }
};

const syncWaitingToTicket: ISagaFunc<{ staffId: string, checkInId: string }> = function* ({ payload }) {
  yield put(uiActions.setLoadingPage(true));
  try {
    const res: IResponseDataBody<boolean> = yield call(apis.syncWaitingToTicket, payload.staffId, payload.checkInId);
    if (res.data.data) {
      yield all([fork(waitingList), fork(availableList), fork(pendingTicket)]);
    }
  } catch (error) { }
  finally {
    yield put(uiActions.setLoadingPage(false));
  }
};

const syncWaitingToTicketV2 = function* () {
  yield put(uiActions.setLoadingPage(true));
  try {
    yield all([fork(waitingList), fork(availableList), fork(pendingTicket)]);
  } catch (error) { }
  finally {
    yield put(uiActions.setLoadingPage(false));
  }
};

const walkInTicketChangeStaff: ISagaFunc<{ id: string, staffId: string }> = function* ({ payload }) {
  yield put(uiActions.setLoadingPage(true));
  try {
    const res: IResponseDataBody<IWaitingListResItem> = yield call(apis.walkInTicketChangeStaff, payload.id, payload.staffId);
    if (res.data.data) {
      yield put(actions.walkInTicketChangeStaff.success(res.data.data));
    }
  } catch (error) { }
  finally {
    yield put(uiActions.setLoadingPage(false));
  }
};

const getAppointmentToday = function* ({ }) {
  yield delay(200);

  try {
    const res: IResponseDataBody<boolean> = yield call(apis.getAppointmentSlideHome);
    if (res.data.data) {
      yield put(actions.getAppointmentToday.success(res.data.data));
    }
  } catch (error) {
    yield put(actions.getAppointmentToday.fail({}));
  }
};

const getSortStaffListAvailableWorking: ISagaFunc<HOME_SORT_BY> = function* ({ payload }) {
  yield setLoading(PATH_LOADING.getListStaffAvailable, true);
  yield delay(100);
  try {
    const res: IResponseDataBody<IStaffAvailableResItem[]> = yield call(apis.getListAvailableWorking, payload);
    if (res.data.data) {
      yield put(actions.getListAvailableWorking.success(res.data.data));
      yield put(turnActions.getTurnStaffsAvailable.success(res.data.data));
    }
  } catch (error) { }
  finally {
    yield setLoading(PATH_LOADING.getListStaffAvailable, false);
  }
};

const getHomeBackground = function* () {
  yield delay(100);
  try {
    const res: IResponseDataBody<string> = yield call(apis.getHomeBackground);
    if (res.data.data) {
      yield put(actions.getHomeBackground.success(res.data.data));
    }
  } catch (error) { }
};

const changeHomeBackground: ISagaFunc<string> = function* ({ payload }) {
  yield put(uiActions.setLoadingPage(true));
  try {
    const res: IResponseDataBody<boolean> = yield call(apis.changeHomeBackground, payload);
    if (res.data.data) {
      yield put(actions.changeHomeBackground.success(payload));
    }
  } catch (error) { }
  finally { yield put(uiActions.setLoadingPage(false)); }
};

const getNotifications = function* () {
  yield delay(200);
  try {
    const res: IResponseDataBody<INotification[]> = yield call(apis.getNotifications);
    if (res.data.data) {
      yield put(actions.getNotifications.success(res.data.data));
    }
  } catch (error) { }
};

const countUnreadNotification = function* () {
  yield delay(200);
  try {
    const res: IResponseDataBody<number> = yield call(apis.countUnreadNotification);
    if (res.data.data) {
      yield put(actions.countUnreadNotification.success(res.data.data));
    }
  }
  catch (error) { }
};

const getLastTicket = function* () {
  yield delay(200);
  try {
    const res: IResponseDataBody<IBillWaitingTicketResItem> = yield call(apis.getLastTicket);
    if (res.data.data) {
      yield put(actions.getLastTicket.success(res.data.data));
    }
  } catch (error) { }
};

const getCouponMarketing = function* () {
  try {
    const res: IResponseDataBody<ICouponPrintDiscount[]> = yield call(apis.getCouponMarketing);
    if (res.data.data) {
      yield put(actions.getCouponMarketing.success(res.data.data));
    }
  } catch (error) {
    yield put(actions.getCouponMarketing.fail({}));
  }
};

const getCheckInHistories: ISagaFunc<IApiCheckInHistoriesParams> = function* ({ payload }) {
  yield delay(100);
  try {
    const res: IResponseTableDataBody<IHistoryCheckInResData> = yield call(apis.getCheckInHistories, payload);
    if (res?.data?.data) {
      yield put(actions.getCheckInHistories.success(res.data.data));
    }
  } catch (error) {
    yield put(actions.getCheckInHistories.fail({}));
  }
};

const getCheckInHistoriesSummary: ISagaFunc<IApiCheckInHistoriesSummaryParams> = function* ({ payload }) {
  yield delay(100);
  try {
    const res: IResponseDataBody<IHistoryCheckInResData> = yield call(apis.getCheckInHistoriesSummary, payload);
    if (res?.data?.data) {
      yield put(actions.getCheckInHistoriesSummary.success(res.data.data));
    }
  } catch (error) {
    yield put(actions.getCheckInHistoriesSummary.fail({}));
  }
};

const getAptGroupByTime = function* ({ }) {
  yield delay(200);
  try {
    const res: IResponseDataBody<any> = yield call(apis.getAptGroupByTime);
    if (res?.data?.data) {
      yield put(actions.getAptGroupByTime.success(res.data.data));
    }
  } catch (error) {
    yield put(actions.getAptGroupByTime.fail({}));
  }
};

const getBillWaitingTicketSuccessLayout: ISagaFunc<IBillWaitingTicketResItem[]> = function* ({ payload }) {
  const allSetting: IShopAllSetting = yield select((state: RootState) => state.shop.allSetting);
  if (!allSetting?.autoScaleLayoutHome || allSetting.layoutHomeCode === HomeLayoutCode.LAYOUT_4) {
    return;
  }
  if (payload.length > 15) {
    yield put(shopActions.changeHomeLayout(HomeLayoutCode.LAYOUT_3));
  } else {
    yield put(shopActions.changeHomeLayout(HomeLayoutCode.LAYOUT_1));
  }
};

const getListStaffAttendance = function* () {
  yield delay(100);
  try {
    const res: IResponseDataBody<any> = yield call(apis.getListStaffAttendance);
    if (res?.data?.data) {
      yield put(actions.getListStaffAttendance.success(res.data.data));
    }
  } catch (error) {
    yield put(actions.getListStaffAttendance.fail({}));
  }
  finally {
  }
};

export default function* userServiceSagas() {
  yield takeLatest(actions.doFilterData, doFilterData);
  yield takeLatest(actions.getWaitingList.fetch, getWaitingList);
  yield takeLatest(actions.getSummaryData.fetch, getSummaryData);
  yield takeLatest(actions.getBillWaitingTicket.fetch, getBillWaitingTicket);
  yield takeLatest(actions.doClockIn.fetch, doClockIn);
  yield takeLatest(actions.doClockOut.fetch, doClockOut);
  yield takeLatest(actions.doClockIn.success, getHistoryClockInOut);
  yield takeLatest(actions.doClockOut.success, getHistoryClockInOut);
  yield takeLatest(actions.getListStaffAvailable.fetch, getListStaffAvailable);
  yield takeLatest(actions.getHistoryClockInOut.fetch, getHistoryClockInOut);
  yield takeLatest(actions.setHistoryClockInOutParams, setHistoryClockInOutParams);
  yield takeLatest(actions.removePendingTicket, removePendingTicket);
  yield takeLatest(actions.removeWaitingTicket, removeWaitingTicket);
  yield takeLatest([actions.getListAvailableWorking.fetch, actions.getListAvailableWorking.fetchPaging], getListAvailableWorking);
  yield takeLatest(actions.syncWaitingToTicket, syncWaitingToTicket);
  yield takeLatest(actions.syncWaitingToTicketV2, syncWaitingToTicketV2);
  yield takeLatest(userActions.getAppointmentToday.fetch, getAppointmentToday);
  yield takeLatest(actions.walkInTicketChangeStaff.fetch, walkInTicketChangeStaff);
  yield takeLatest(userUIActions.setHomeSort, getSortStaffListAvailableWorking);
  yield takeLatest(actions.getHomeBackground.fetch, getHomeBackground);
  yield takeLatest(actions.changeHomeBackground.fetch, changeHomeBackground);
  yield takeLatest(actions.getNotifications.fetch, getNotifications);
  yield takeLatest(actions.countUnreadNotification.fetch, countUnreadNotification);
  yield takeLatest(actions.getLastTicket.fetch, getLastTicket);
  yield takeLatest(actions.getCouponMarketing.fetch, getCouponMarketing);
  yield takeLatest(actions.getCheckInHistories.fetch, getCheckInHistories);
  yield takeLatest(actions.getCheckInHistoriesSummary.fetch, getCheckInHistoriesSummary);
  yield takeLatest(actions.getBillWaitingTicket.success, getBillWaitingTicketSuccessLayout);
  yield takeLatest(actions.getAptGroupByTime.fetch, getAptGroupByTime);
  yield takeLatest(actions.getListStaffAttendance.fetch, getListStaffAttendance);
}
